import type { ApolloQueryResult, Operation } from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useChangeRequestFileUpdate } from '@risksmart-app/components/src/file/useFileUpdate';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { isRecoverableRefreshTokenError } from '@risksmart-app/components/src/utils/errorUtils';
import type { GetActionByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionByIdDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GraphQLError } from 'graphql';
import { createClient } from 'graphql-ws';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ConfirmChangeRequestModal } from 'src/components/change-requests-preview/ConfirmChangeRequestModal';

import { handleError } from '@/utils/errorUtils';
import { evictField } from '@/utils/graphqlUtils';

import apolloMetricLink from './apolloMetricLink';

export default function ApolloGraphqlProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { addNotification } = useNotifications();
  const { getAccessTokenSilently, getIdTokenClaims } = useRisksmartUser();
  const [
    confirmChangeRequestModalVisible,
    setConfirmChangeRequestModalVisible,
  ] = useState(false);
  const [approvalOperation, setApprovalOperation] = useState<null | Operation>(
    null
  );

  const errorLink = useMemo(
    () =>
      // Log any GraphQL errors or network error that occurred
      //https://www.apollographql.com/docs/react/api/link/apollo-link-error
      onError(({ graphQLErrors, networkError, operation, forward }) => {
        // RSP-6931: tag every captured GraphQL error with its operation name so
        // Sentry alerts/dashboards can filter by operation (e.g. issue alert on
        // `graphql_operation` containing "Oversight"). Generic — no per-feature
        // logic in this bridge layer; the feature selection lives in the Sentry
        // alert query, not here.
        // Anonymous GraphQL ops have an empty operationName; normalise so the
        // Sentry tag / transaction name is never blank.
        const operationName = operation.operationName || 'anonymous';
        const operationTags = { graphql_operation: operationName };
        if (graphQLErrors) {
          // Note: types are not correct, as graphqlErrors appears to be a simple object in some (maybe all cases) rather then an Exception like object
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          graphQLErrors.forEach((error: any) => {
            if (
              error.message.includes(
                'You need to create a change request to perform this action.'
              )
            ) {
              setConfirmChangeRequestModalVisible(true);
              setApprovalOperation(operation);

              return false;
            }

            const captureContext = {
              extra: {
                operationName: operationName,
              },
              tags: operationTags,
            };
            if (error instanceof GraphQLError) {
              handleError(error, captureContext, operationName);
            } else if ('message' in error) {
              // Avoids the "Object captured as exception with keys: extensions, message" message in Sentry, and get a unique error message instead
              handleError(
                error.message,
                {
                  extra: {
                    operationName: operationName,
                    ...error,
                  },
                  tags: operationTags,
                },
                operationName
              );
            } else {
              handleError(error, captureContext, operationName);
            }
          });
        }
        if (networkError) {
          // Retry once silently before reporting. Apollo's onError fires again
          // on the retry, so we guard against an infinite loop with a context
          // flag. Only capture if the retry also fails — this filters out the
          // ambient "TypeError: Failed to fetch" noise from tab-close /
          // navigation / transient network that resolves on retry, while still
          // reporting real backend / CORS / DNS failures that persist.
          const { retried } = operation.getContext();
          if (!retried) {
            operation.setContext({ retried: true });

            return forward(operation);
          }

          // RSP-6931: a backend 500 surfaces here as an Apollo networkError with
          // a `statusCode`. Tag it (`http_status`) so Sentry alerts can isolate
          // 5xx — e.g. a Slack alert on `graphql_operation:*Oversight*
          // http_status:500`. This is the reliable capture point for backend
          // 500s: the rest-api errorHandler swallows HttpError 5xx into Datadog
          // logs without a Sentry event, but the browser always sees the 500.
          const httpStatus =
            'statusCode' in networkError ? networkError.statusCode : undefined;

          handleError(
            networkError,
            {
              extra: {
                operationName: operationName,
              },
              tags: httpStatus
                ? { ...operationTags, http_status: String(httpStatus) }
                : operationTags,
            },
            operationName
          );
        }
      }),
    []
  );

  const getAuthHeaders = useCallback(async () => {
    let accessToken = null;
    let claims = null;
    const headers: Record<string, string> = {};

    try {
      accessToken = await getAccessTokenSilently();
      claims = await getIdTokenClaims();
    } catch (error) {
      // Missing/invalid refresh tokens are expected session-lifecycle events —
      // swallow them so the finally block redirects to /logout instead of the
      // error crashing the page through the error boundary (RSP-4478).
      if (!isRecoverableRefreshTokenError(error)) {
        throw error;
      }
    } finally {
      // Logout if access token is not available
      if (!accessToken) {
        window.location.href = '/logout';
      }
    }

    if (accessToken) {
      headers.authorization = `Bearer ${accessToken}`;
    }
    if (claims?.claims_tenant) {
      const tenant = claims.claims_tenant;
      headers['x-tenant-name'] = tenant.toLowerCase();
    }

    return { headers };
  }, [getAccessTokenSilently, getIdTokenClaims]);

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }) => {
      const { headers: newHeaders } = await getAuthHeaders();
      const combinedHeaders = {
        ...headers,
        ...newHeaders,
      };

      return { headers: combinedHeaders };
    });

    const customUrlFetch = async (
      uri: RequestInfo | URL,
      options: RequestInit | undefined
    ) => {
      uri = getEnv('REACT_APP_TENANT_API_URL') + '/v1/graphql';

      if (options) {
        options.mode = 'cors';
      }

      return fetch(uri, options);
    };

    const httpLink = new HttpLink({
      fetch: customUrlFetch,
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: async () => {
          const environment = getEnv('REACT_APP_ENVIRONMENT', true);
          if (environment === 'dev-local' || environment === 'ephemeral') {
            return getEnv('REACT_APP_WS_TENANT_API_URL');
          }
          const { headers } = await getAuthHeaders();
          const tenantName = headers['x-tenant-name'];
          const hostName = getEnv('REACT_APP_WS_TENANT_API_URL');

          return `${hostName}/${tenantName}/v1/graphql`;
        },
        connectionParams: async () => {
          const { headers } = await getAuthHeaders();

          return { headers };
        },
      })
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink
    );

    return new ApolloClient({
      // link: authLink.concat(httpLink),
      // set to true to debug caching issues in dev
      devtools: { enabled: false },
      link: ApolloLink.from([apolloMetricLink, errorLink, authLink, splitLink]),
      cache: new InMemoryCache(),
    });
  }, [errorLink, getAuthHeaders]);

  const { updateFiles } = useChangeRequestFileUpdate();

  return (
    <ApolloProvider client={client}>
      {confirmChangeRequestModalVisible && (
        <ConfirmChangeRequestModal
          onDismiss={() => setConfirmChangeRequestModalVisible(false)}
          onConfirm={(requesterComment) => {
            if (!approvalOperation) {
              return;
            }
            toast.dismiss();
            addNotification({
              type: 'promise',
              // TODO: translation
              successMessage: 'Change request confirmed',
              // TODO: translation
              errorMessage: 'Failed to confirm change request',
              promise: (async () => {
                const result = await client.mutate({
                  mutation: approvalOperation.query,
                  variables: approvalOperation.variables,
                  context: {
                    ...approvalOperation.getContext(),
                    headers: {
                      ...approvalOperation.getContext()?.headers,
                      'x-confirm-change-request': 'true',
                      ...(requesterComment
                        ? {
                            'x-requester-comment':
                              encodeURIComponent(requesterComment),
                          }
                        : {}),
                    },
                  },
                });

                const parentId =
                  result?.data?.updateChildAction?.change_request_id;

                if (
                  parentId &&
                  (approvalOperation.operationName === 'updateAction' ||
                    approvalOperation.operationName === 'closeAction')
                ) {
                  const action: ApolloQueryResult<GetActionByIdQuery> =
                    await client.query({
                      query: GetActionByIdDocument,
                      variables: { _eq: approvalOperation.variables.Id },
                      fetchPolicy: 'no-cache',
                    });

                  const originalFiles = action?.data?.action[0]?.files || [];

                  await updateFiles({
                    parentType: Parent_Type_Enum.ChangeRequest,
                    parentId: parentId,
                    originalFiles: originalFiles
                      .map((originalFile) => originalFile.file)
                      .filter((originalFile) => !!originalFile),
                    selectedFiles: approvalOperation?.variables?.files,
                  });
                }

                evictField(client.cache, 'change_request');

                return client.refetchQueries({ include: ['all'] });
              })(),
            });
          }}
        />
      )}
      {children}
    </ApolloProvider>
  );
}
